import React, {useEffect, useRef, useState} from 'react';
import './search-location-input.scss';
import locationPin from '../../../../assets/icons/location-pin.svg';
import cross from '../../../../assets/icons/cross.svg';
import {AppVersionForTenants, Config, TenantAppVersionConfig} from "../../../../../constants/Config";
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../../../../store/configureStore';
import searchIconLight from "../../../../assets/icons/search-icon-light.svg";
import {clubFinderSearchBarSliceActions} from '../../../../../store/slices/clubFinderSearchBarSlice';
import {ClubFinderLocationResultTypes} from '../../../../../types/ApiTypeDefinition/clubFinderLocationSearchResultTypes';
import Loader from "../../../Loader/loader";
import {getSelectedFilterCount} from "../../../../../utilities/getSelectedFilterCount";

const baseClass = 'clubFinder-searchLocationInputBox';
const MobileViewWidth = Config.breakpoints.large.width;

interface PropTypes {
  inputBoxIcon?: string;
  label: string;
  placeholder?: string
  onChangeHandler: React.ChangeEventHandler<HTMLInputElement>
  inputFocusStatusHandler?: (status: boolean) => void
  isResultItemClicked?: boolean
  setResultItemClicked?: any
  inputEditableStatusHandler: (status: boolean) => void
  search?: any
  searchButtonPressed?: boolean
  openFilterDrawer?: any
  getUserCurrentLocation?: any
  presetValue: string
  isResultSetVisible: boolean
}

const SearchLocationInput = (props: PropTypes): JSX.Element => {
  const dispatch = useDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputFocused, setInputFocus] = useState(false);
  const selectedLocation = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedLocation);
  const isLocationSearchActive = useSelector((state: RootState) => state.clubFinderSearchBarStore.activeSearchFilterMobile.locationFilter);
  const searchOptionsLoaded = useSelector((state: RootState) => state.clubFinderSearchBarStore.searchOptionsLoaded);
  const [selectedLocationText, setSelectedLocationText] = useState('');
  const [isEditable, setEditable] = useState(false);
  const parentWidth = useSelector((state: RootState) => state.widgetConfigurationSettings.widgetParentWidth);
  const [inputValue, setInputValue] = useState('');
  const [clearButtonAcquiredWidth, setClearButtonAcquiredWidth] = useState('0px');
  const tenant = useSelector((state: RootState) => state.widgetConfigurationSettings.tenant);
  const isSelectionSearchable = useSelector((state: RootState) => state.clubFinderSearchBarStore.isSearchable);
  const isFilterButtonVisible = useSelector((state: RootState) => state.clubFinderSearchBarStore.isFilterButtonVisible);
  const userLocationAddress = useSelector((state: RootState) => state.clubFinderSearchBarStore.userLocationAddress);
  const isUserLocationSearching = useSelector((state: RootState) => state.clubFinderSearchBarStore.isUserLocationSearching);
  const selectedFormatFilterName = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedFormatFilterName);
  const selectedMultiGenderFilters = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedMultiGenderFilters);
  const selectedInclusionFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedInclusionFilters);
  const selectedDaysFilter = useSelector((state: RootState) => state.clubFinderSearchBarStore.selectedDayFilters);

  const clearInput = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setInputValue('');
    setClearButtonAcquiredWidth('0px');
    dispatch(clubFinderSearchBarSliceActions.setIsSearchable(false));
  }

  const handleClearButtonAcquiredWidth = () => {
    if (parentWidth > MobileViewWidth) {
      setClearButtonAcquiredWidth('24px');
    } else {
      setClearButtonAcquiredWidth('80px');
    }
  }

  const getSelectedLocationText = (selectedLocation: ClubFinderLocationResultTypes | null): string => {
    let locationText = '';
    if (selectedLocation && selectedLocation.orgName) {
      locationText = selectedLocation.orgName;
    }
    if (selectedLocation?.address && selectedLocation?.address.fullAddress) {
      locationText = selectedLocation.address.fullAddress;
    } else if (selectedLocation && selectedLocation.address && selectedLocation.address.suburb) {
      locationText = locationText.length > 0 ? `${locationText}, ${selectedLocation.address.suburb}` : selectedLocation.address.suburb;
    }
    return locationText;
  }

  useEffect(() => {
    let locationText = getSelectedLocationText(selectedLocation);
    setSelectedLocationText(locationText);
    setInputValue(locationText);
    if (locationText && locationText.length) {
      handleClearButtonAcquiredWidth();
    }
    if (AppVersionForTenants.v2.tenants.includes(tenant)) {
      dispatch(clubFinderSearchBarSliceActions.setUserLocationAddress(null))
    }
  }, [selectedLocation]);

  useEffect(() => {
    setEditable(isLocationSearchActive);
  }, [isLocationSearchActive]);

  useEffect(() => {
    if (props.isResultItemClicked) {
      setEditable(false);
      props.inputEditableStatusHandler(false);
    }
  }, [props.isResultItemClicked, props.inputEditableStatusHandler]);

  const onFocusHandler = () => {
    setInputFocus(true);
    if (props.inputFocusStatusHandler) {
      props.inputFocusStatusHandler(true);
    }

    if (inputValue && inputValue.length) {
      handleClearButtonAcquiredWidth();
    }
  }

  const onBlurHandler = () => {
    setInputFocus(false);
    if (props.inputFocusStatusHandler) {
      props.inputFocusStatusHandler(false);
    }
    if (inputValue && inputValue.length) {
      handleClearButtonAcquiredWidth();
    }
  }

  /*
    Handle focus for location search results selection
    on a desktop view
   */
  const inputContainerClickedHandler = () => {
    if (parentWidth > MobileViewWidth) {
      setInputFocus(true);
      if (props.inputFocusStatusHandler) {
        props.inputFocusStatusHandler(true);
      }
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    }
  }

  const inputOnchangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    props.onChangeHandler(event)
    if (event.target.value && event.target.value.length) {
      handleClearButtonAcquiredWidth();
    } else {
      setClearButtonAcquiredWidth('0px');
    }
  }

  /*
     Handle actions for button links on input container
     in mobile view
   */
  const rightInputActionHandler = () => {
    props.inputEditableStatusHandler(!isEditable);
    if (props.setResultItemClicked) {
      props.setResultItemClicked(false);
    }
    if (inputValue && inputValue.length) {
      handleClearButtonAcquiredWidth();
    }
  }

  useEffect(() => {
    if (AppVersionForTenants.v2.tenants.includes(tenant)) {
      props.inputEditableStatusHandler(true)
    }
  }, [])

  useEffect(() => {
    if (AppVersionForTenants.v2.tenants.includes(tenant) && userLocationAddress) {
      let inputValue = "";

      if (userLocationAddress.route) {
        if (inputValue) {
          inputValue += " ";
        }
        inputValue += userLocationAddress.route;
      }

      if (userLocationAddress.suburb) {
        if (inputValue) {
          inputValue += ", ";
        }
        inputValue += userLocationAddress.suburb;
      }

      if (userLocationAddress.state) {
        if (inputValue) {
          inputValue += " ";
        }
        inputValue += userLocationAddress.state;
      }

      if (userLocationAddress) {
        setInputValue(inputValue)
      }
      props.setResultItemClicked(true);
      dispatch(clubFinderSearchBarSliceActions.setFilterButtonVisible(true));
      dispatch(clubFinderSearchBarSliceActions.setIsSearchable(true));

      if (parentWidth <= MobileViewWidth) {
        setEditable(false);
        setSelectedLocationText(inputValue);
      }
    }
  }, [userLocationAddress, tenant]);

  const totalSelectedFilters = getSelectedFilterCount(selectedFormatFilterName, selectedMultiGenderFilters, selectedInclusionFilter, selectedDaysFilter);

  return (
    <div
      onClick={inputContainerClickedHandler}
      className={`${baseClass} ${tenant}
      ${isInputFocused ? 'is-focused' : 'not-focused'}
      ${searchOptionsLoaded ? baseClass + '--no-border' : ''}
      ${AppVersionForTenants.v2.tenants.includes(tenant) ? 'floating-bar' : ''}
      ${props.isResultSetVisible ? 'result-set-visible' : 'result-set-not-visible'}`}>
      <div className={`${baseClass}--left-icon`} onClick={AppVersionForTenants.v2.tenants.includes(tenant) ? props.getUserCurrentLocation : null}>
        {isUserLocationSearching && AppVersionForTenants.v2.tenants.includes(tenant) && (
          <Loader />
        )}
        {!isUserLocationSearching && (
          <img src={locationPin} alt="Location Pin"/>
        )}
      </div>
      <div className={`${baseClass}--input-container`}>
        <div className={`${baseClass}--input-container-label`}>
          <span>{props.label}</span>
          {parentWidth <= MobileViewWidth && (
            <div className={`${baseClass}--right-input-action`} onClick={() => rightInputActionHandler()}>
              {isEditable ? 'Cancel' : (selectedLocation || userLocationAddress) ? 'Edit' : 'Add'}
            </div>
          )}
        </div>
        {(selectedLocation || userLocationAddress) && !isEditable && parentWidth <= MobileViewWidth && (
          <div className={`${baseClass}--input-container-selected`}>
            {selectedLocationText}
          </div>
        )}
        {((parentWidth <= MobileViewWidth && isEditable && !props.isResultItemClicked) || (parentWidth > MobileViewWidth)) && (
          <div className={`${baseClass}--input-container-input-box ${isInputFocused ? 'is-focused' : 'not-focused'}`}
               style={{'--clear-button-acquired-width': clearButtonAcquiredWidth} as React.CSSProperties}>
            {props.inputBoxIcon && (
              <div className={`${baseClass}--input-box-icon`}>
                <img src={props.inputBoxIcon} alt="logo"/>
              </div>
            )}
            <input
              type='text'
              ref={inputRef}
              placeholder={props.placeholder}
              onFocus={() => onFocusHandler()}
              onBlur={() => onBlurHandler()}
              onChange={inputOnchangeHandler}
              value={inputValue}
            />
          </div>
        )}
        {((parentWidth <= MobileViewWidth && isEditable) || (parentWidth > MobileViewWidth)) && (
          <div onClick={clearInput}
               className={`${baseClass}--clear-text-icon ${inputValue && inputValue.length ? baseClass + '--clear-text-icon-visible' : ''}`}>
            <img src={cross} alt="clear"/>
          </div>
        )}
      </div>
      {AppVersionForTenants.v2.tenants.includes(tenant) && (
        <>
          {(props.searchButtonPressed || isFilterButtonVisible) && (
            <div
              className={`${baseClass}--filter-button ${totalSelectedFilters > 0 ? 'filters-selected' : ''}`}>
              <button onClick={isSelectionSearchable ? props.openFilterDrawer : () => void (0)}>
                <img src={TenantAppVersionConfig[tenant].icons.settings} alt={'settings-icon'}/>
                {totalSelectedFilters > 0 && (
                  <span className={'filter-count'}>{totalSelectedFilters}</span>
                )}
                <span className={'filter-button-label'}>Filter</span>
              </button>
            </div>
          )}
          <div
            className={`${baseClass}--search-button ${baseClass}--search-button-${isSelectionSearchable ? 'searchable' : 'not-searchable'}`}>
            <button onClick={props.search}>
              <img src={searchIconLight} alt={'search icon'}/>
              <span className={'search-button-label'}>Search</span>
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default SearchLocationInput;
